import { CartAPI, createAsyncQueue } from "@lemmonhq/orders";
import { nanoid } from "nanoid";
import { ref, watch, toRaw } from "vue";

import { useCustomer } from "../../Auth";
import { RealtimeAdapter, CouchDBAdapter } from "./adapters/index";
import { GroupMessage, GroupState } from "./types";
import { appendMessage, serializeState } from "./utils";
import { GroupOrder } from "@lemmonhq/types";

export * from "./types";
export * from "./utils";

export function useRealtime(
  customer: ReturnType<typeof useCustomer>,
  cart: CartAPI,
  tableSlug?: string,
) {
  const channelId = ref<string>(nanoid());
  const enabled = ref(false);
  const eventLog = ref<GroupMessage[]>([]);
  const groupState = ref<GroupState>({
    members: [],
    finalizedCheckouts: [],
    confirmedCheckouts: [],
  });

  let adapter: RealtimeAdapter | undefined;

  const connectionQueue = createAsyncQueue(1);
  const isConnecting = ref();

  const connect = async () => {
    if (!customer.customer.value?.id) {
      throw new Error("Customer not authenticated");
    }

    // Create adapter
    if (import.meta.env.VITE_LOCALHOST === "true") {
      adapter = new CouchDBAdapter();
    } else {
      //      adapter = new AblyAdapter(
      //        import.meta.env.VITE_ABLY_KEY,
      //        customer.customer.value.id
      //      );
      adapter = new CouchDBAdapter();
    }

    await adapter.createChannel(channelId.value);

    // Load historical messages and construct initial state
    const { messages } = await adapter.readHistory();
    eventLog.value = messages;
    const initialState = serializeState(messages);

    groupState.value = initialState;

    // Subscribe to live messages
    await adapter.subscribeToMessages((message) => {
      //console.log("Received message:", message);
      // Add message to event log
      eventLog.value = appendMessage(eventLog.value, message);
      // Recompute state from full event log
      groupState.value = serializeState(eventLog.value);
    });

    // Send JOIN message
    await sendMessage({
      type: "JOIN",
      clientId: customer.customer.value.id,
      cart: toRaw(cart.cart),
      timestamp: Date.now(),
    });

    return initialState;
  };

  // Watch for cart changes and send updates
  watch(
    cart.cart,
    async (newCart) => {
      if (adapter && customer.customer.value?.id) {
        const message: GroupMessage = {
          type: "CART_UPDATE",
          clientId: customer.customer.value.id,
          cart: toRaw(newCart),
          timestamp: Date.now(),
        };

        // Add to local event log immediately for optimistic update
        eventLog.value = appendMessage(eventLog.value, message);
        groupState.value = serializeState(eventLog.value);

        // Send message to other clients
        try {
          await sendMessage(message);
        } catch (error) {
          console.error("Failed to send cart update:", error);
          // Could implement rollback logic here if needed
        }
      }
    },
    { deep: true },
  );

  const sendMessage = async (message: GroupMessage): Promise<void> => {
    if (!adapter) {
      throw new Error("Adapter not initialized");
    }
    await adapter.sendMessage(message);
  };

  const disconnect = async () => {
    console.log("disconnecting");

    // Send LEAVE message before disconnecting
    if (adapter && customer.customer.value?.id) {
      try {
        await sendMessage({
          type: "LEAVE",
          clientId: customer.customer.value.id,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error("Error sending LEAVE message:", error);
      }
    }
    await adapter?.close();
    // Clear state and event log
    //    eventLog.value = [];
    //    groupState.value = {
    //      members: [],
    //      finalizedCheckouts: [],
    //      confirmedCheckouts: [],
    //    };
    adapter = undefined;
  };

  const enable = async (groupOrder: GroupOrder) => {
    console.log("enabling");
    if (!customer.customer.value) {
      throw new Error("no customer");
    }
    channelId.value = groupOrder.id;
    isConnecting.value = true;
    console.log("enabling2");
    return connectionQueue.push(async () => {
      try {
        await connect();
        enabled.value = true;
      } catch (e) {
        console.error(e);
      } finally {
        isConnecting.value = false;
      }
    }, "enable");
  };

  const disable = async () => {
    if (!customer.customer.value) return;
    return connectionQueue.push(async () => {
      await disconnect();
      enabled.value = false;
    }, "disable");
  };

  return {
    channelId,
    enable,
    disable,
    enabled,
    groupState,
    sendMessage,
    eventLog,
    isConnecting,
    getState: async () => {
      if (adapter) {
        const { messages } = await adapter.readHistory();
        eventLog.value = messages;
        const state = serializeState(messages);
        groupState.value = state;
        return state.members;
      }
      return [];
    },
  };
}
